import { fabric } from 'fabric';
import {
  FabricEditor,
  fillGradientPlugin,
  fontLoadingPlugin,
  gridPlugin,
  layerPlugin,
  rectCornersPlugin,
  selectionPlugin,
} from 'modules/fabric-editor';
import type { Object as FabricObject } from 'fabric/fabric-impl';
import { layoutBlocksPlugin } from 'modules/manager/domain/layout-blocks';
import { rebaseAssetUrls } from 'modules/manager/domain/template-file';
import { placeholderPlugin } from 'modules/manager/domain/placeholder';

/**
 * Plugin set for an off-screen canvas that only has to *draw* a template — no selection UI,
 * no grid. Used by the preset thumbnail preview; the render itself lives on the backend
 * (`POST /api/render-template`), see `.claude/docs/media-manager-server-render.md`.
 */
export const RENDER_PLUGINS = [
  selectionPlugin(),
  layerPlugin(),
  gridPlugin({ visible: false }),
  rectCornersPlugin(),
  fillGradientPlugin(),
  fontLoadingPlugin(),
  placeholderPlugin(),
  layoutBlocksPlugin(),
];

/**
 * Puts a template's objects on an off-screen canvas — what `canvas.loadFromJSON` does, minus the
 * trap: objects come to life asynchronously (images, fonts), and `loadFromJSON` then calls
 * `canvas.clear()` itself, before our callback. If the preview was unmounted meanwhile, the canvas is
 * disposed, its context is `null`, and `clearRect` throws (IMGM-1521). So the objects are enlivened
 * here, and a disposed editor resolves without touching the canvas.
 *
 * The rest mirrors `loadFromJSON` → `__setupCanvas` (fabric 5.5): `clear()`, `insertAt` with
 * `renderOnAddRemove` off, one `renderAll()`. A template stores only `objects` and `version`, so
 * there is no background, overlay or clip path to restore.
 */
export function loadCanvasJson(editor: FabricEditor, objects: unknown[]): Promise<void> {
  // Same as the editor restore: the logo `src` may point at the stand the template was saved on.
  const rebased = rebaseAssetUrls(objects as FabricObject[], window.location.origin);
  return new Promise((resolve) => {
    fabric.util.enlivenObjects(
      rebased,
      (enlivened: FabricObject[]) => {
        if (editor.isDisposed()) {
          resolve();
          return;
        }
        const { canvas } = editor;
        const renderOnAddRemove = canvas.renderOnAddRemove;
        canvas.renderOnAddRemove = false;
        canvas.clear();
        enlivened.forEach((object, index) => canvas.insertAt(object, index, false));
        canvas.renderOnAddRemove = renderOnAddRemove;
        canvas.renderAll();
        editor.emit('canvas:loaded', { source: 'render' });
        resolve();
      },
      // Empty namespace = the `fabric` one, as `canvas._enlivenObjects` passes it.
      '',
    );
  });
}

export function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 16);
    }
  });
}
